import { KeyValuePair } from 'helpers';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TicketPriority, UserTypology } from 'helpers/Enums';
import { Dictionary } from 'lodash';

interface IApplicationContext {
    isLoading: boolean
    ticketCategories: ITicketCategory[]
    ticketSubCategories: ITicketSubCategory[]
    ticketPriorities: KeyValuePair<TicketPriority, string>[]
    ticketStatuses: ITicketStatus[]
    simecUsers: IUser[]
    userGroups: KeyValuePair<string, string>[]
    countries: ICountry[]
    toolTypologies: IToolTypology[]

    getSimecReferenceData: () => Promise<void>
    getCategoryById: (id: string) => ITicketCategory | undefined
    getSubCategoryById: (id: string) => ITicketSubCategory | undefined
}

interface IToolTypology {
    toolTypologyId: string
    name: string
    needMaintenance: boolean
}

interface ITicketCategory {
    ticketCategoryId: string
    name: Dictionary<string>,
    color: string,
    fromCustomer: boolean
    subCategories: ITicketCategorySubCategory[]
}

interface ITicketCategorySubCategory {
    id: string
    defaultNotification: string[]
    names: Dictionary<string>
}

interface ITicketSubCategory {
    id: string

    // category data
    categoryTicketCategoryId: string
    categoryNames: Dictionary<string>,
    categoryColor: string,
    categoryFromCustomer: boolean

    // subcategory data
    defaultNotification: string[]
    names: Dictionary<string>
    canBeSelectedByCustomer: boolean
}

interface ITicketStatus {
    code: string
    descriptions: Dictionary<string>
}

interface IUser {
    userId: string
    firstName: string
    lastName: string
    fullName: string
    email: string
    typology: UserTypology
    isDisabled: boolean
    simecClaims: SimecClaim
}

interface SimecClaim {
    canCreateTicket: boolean
    canBeReporter: boolean
    canDeleteTicket: boolean
    canViewTicket: boolean
    canBeAssignedToTicket: boolean
    canGoToCustomer: boolean
    canManageCustomerUsers: boolean
    canManageSimecUsers: boolean
    canManageTicketInbox: boolean
    canViewSummary: boolean
}

interface ICountry {
    countryCode: string
    countryName: string
}

export let applicationContext = React.createContext({} as IApplicationContext);

let { Provider } = applicationContext;

let ApplicationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    let [isLoading, setLoading] = useState(true);

    let [ticketPriorities, setTicketPriorities] = useState<KeyValuePair<TicketPriority, string>[]>([]);
    let [ticketStatuses, setTicketStatuses] = useState<ITicketStatus[]>([]);
    let [ticketCategories, setTicketCategories] = useState<ITicketCategory[]>([]);
    let [ticketSubCategories, setTicketSubCategories] = useState<ITicketSubCategory[]>([]);
    let [simecUsers, setSimecUsers] = useState<IUser[]>([]);
    let [userGroups, setUserGroups] = useState<KeyValuePair<string, string>[]>([]);
    let [countries, setCountries] = useState<ICountry[]>([]);
    let [toolTypologies, setToolTypologies] = useState<IToolTypology[]>([]);


    useEffect(() => {
        getSimecReferenceData();
    }, []);


    let getSimecReferenceData = async () => {
        try {
            // setLoading(true);

            let res = await axios.get('/api/Application/GetAllSimecReferenceData');

            let { item } = res.data;
            setTicketCategories(item.ticketCategories);
            setTicketSubCategories(item.ticketSubCategories);
            setTicketStatuses(item.ticketStatuses);
            setTicketPriorities(item.ticketPriorities);
            setSimecUsers(item.simecUsers);
            setUserGroups(item.userGroups);
            setCountries(item.countries);
            setToolTypologies(item.toolTypologies);

        } catch (err) { }
        finally {
            setLoading(false);
        }
    }

    let getCategoryById = (id: string) => {
        return ticketCategories.find(x => x.ticketCategoryId === id);
    }

    let getSubCategoryById = (id: string) => {
        return ticketSubCategories.find(x => x.id === id);
    }


    return (
        <Provider value={{
            isLoading,
            ticketCategories,
            ticketSubCategories,
            ticketPriorities,
            ticketStatuses,
            simecUsers,
            userGroups,
            countries,
            toolTypologies,

            getSimecReferenceData,
            getCategoryById,
            getSubCategoryById
        }}>
            {children}
        </Provider>
    )
}

export default ApplicationProvider;